import { useEffect, useState, useRef } from "react";
import type { CSSProperties } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import ImageWithSkeleton from "./ImageWithSkeleton";

interface TaskImageProps {
  src: string;
  alt: string;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  taskId?: string;
  imageIndex?: number;
  kind?: "gallery" | "instruction";
  blurDataUrl?: string;
  className?: string;
  skeletonClassName?: string;
  onClick?: () => void;
  priority?: boolean;
  instanceKey?: string;
  width?: number;
  height?: number;
  srcSet?: string;
  sizes?: string;
  /** True when the backend produced a white-background copy of the source PNG. */
  isNormalizedDiagram?: boolean;
}

// In-memory session cache — one entry per src URL.
const memCache = new Map<string, string>();
// Concurrent request de-dup
const inflight = new Map<string, Promise<string | null>>();

const SS_PREFIX = "blur:";

// Throttle background warming to avoid a request storm on large lists.
const WARM_CONCURRENCY = 3;
let warmActive = 0;
const warmQueue: Array<() => void> = [];
function scheduleWarm(task: () => Promise<unknown>) {
  const run = () => {
    warmActive++;
    task().finally(() => {
      warmActive--;
      const next = warmQueue.shift();
      if (next) next();
    });
  };
  if (warmActive < WARM_CONCURRENCY) run();
  else warmQueue.push(run);
}

function loadFromSession(src: string): string | null {
  try {
    return sessionStorage.getItem(SS_PREFIX + src);
  } catch {
    return null;
  }
}
function saveToSession(src: string, blur: string) {
  try {
    sessionStorage.setItem(SS_PREFIX + src, blur);
  } catch {
    /* quota — ignore */
  }
}

const failedSrcs = new Set<string>();

async function fetchBlur(
  src: string,
  taskId: string | undefined,
  kind: "gallery" | "instruction" | undefined,
  imageIndex: number | undefined
): Promise<string | null> {
  if (memCache.has(src)) return memCache.get(src)!;
  if (failedSrcs.has(src)) return null;
  const ss = loadFromSession(src);
  if (ss) {
    memCache.set(src, ss);
    return ss;
  }
  if (inflight.has(src)) return inflight.get(src)!;

  const p = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("generate-blur-placeholder", {
        body: {
          src,
          taskId,
          kind: kind ?? (taskId ? "gallery" : undefined),
          index: imageIndex ?? 0,
        },
      });
      if (error || !data?.blurDataUrl) {
        failedSrcs.add(src); // don't retry the same image in this session
        return null;
      }
      const blur = data.blurDataUrl as string;
      memCache.set(src, blur);
      saveToSession(src, blur);
      return blur;
    } catch {
      return null;
    } finally {
      inflight.delete(src);
    }
  })();
  inflight.set(src, p);
  return p;
}

/**
 * Proactively warm the blur cache for a batch of tasks after the list loads.
 * Idempotent — memCache + sessionStorage guarantee no duplicate work.
 */
export function warmBlurCache(tasks: Array<{
  id?: string;
  imageUrls?: string[];
  imageBlurData?: string[];
  instructionImageUrl?: string;
  instructionImageBlur?: string;
}>) {
  if (typeof window === "undefined") return;
  for (const t of tasks) {
    if (t.imageUrls?.length) {
      t.imageUrls.forEach((src, i) => {
        if (!src || !/^https?:\/\//.test(src)) return;
        if (t.imageBlurData?.[i]) return;
        if (memCache.has(src) || loadFromSession(src)) return;
        scheduleWarm(() => fetchBlur(src, t.id, "gallery", i));
      });
    }
    const iSrc = t.instructionImageUrl;
    if (iSrc && /^https?:\/\//.test(iSrc) && !t.instructionImageBlur) {
      if (!memCache.has(iSrc) && !loadFromSession(iSrc)) {
        scheduleWarm(() => fetchBlur(iSrc, t.id, "instruction", 0));
      }
    }
  }
}

const GRACE_PERIOD_MS = 180;

/**
 * TaskImage — thin wrapper around ImageWithSkeleton that guarantees a blur
 * placeholder for task images. If `blurDataUrl` isn't provided, it lazily
 * resolves one from cache/session/edge function and re-renders.
 */
const TaskImage = (props: TaskImageProps) => {
  const { src, blurDataUrl, taskId, kind, imageIndex, isNormalizedDiagram: _isNormalizedDiagram, ...rest } = props;
  const [resolvedBlur, setResolvedBlur] = useState<string | undefined>(
    blurDataUrl || memCache.get(src) || loadFromSession(src) || undefined
  );
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (blurDataUrl) {
      setResolvedBlur(blurDataUrl);
      return;
    }
    const cached = memCache.get(src) || loadFromSession(src);
    if (cached) {
      setResolvedBlur(cached);
      return;
    }
    let cancelled = false;
    // Kick off backfill only when we have a real https src
    if (!/^https?:\/\//.test(src)) return;
    // Race the fetch against a short grace period. If the blur arrives
    // in-window we set it immediately (no flash). Otherwise we let the neutral
    // placeholder show and set the blur when it eventually resolves.
    const fetchPromise = fetchBlur(src, taskId, kind, imageIndex);
    let settled = false;
    const grace = setTimeout(() => {
      if (settled) return;
      // give up on the race — placeholder will render; fetch continues below
    }, GRACE_PERIOD_MS);
    fetchPromise.then((blur) => {
      settled = true;
      clearTimeout(grace);
      if (!cancelled && alive.current && blur) setResolvedBlur(blur);
    });
    return () => {
      cancelled = true;
      clearTimeout(grace);
    };
  }, [src, blurDataUrl, taskId, kind, imageIndex]);

  return (
    <div className="w-full h-full bg-task-image-paper">
      <ImageWithSkeleton
        src={src}
        {...rest}
        blurDataUrl={resolvedBlur}
        className={cn(
          rest.className,
          "bg-task-image-paper"
        )}
      />
    </div>
  );
};

export default TaskImage;